import { Dialog as MuiDialog, styled } from '@mui/material';
import { type ReactNode, useId } from 'react';

import { BackIcon, CloseIcon } from '../../../icons';
import { Button } from '../../atoms/Button';
import { IconButton } from '../../atoms/IconButton';

type Props = {
  open: boolean;
  onClose: () => void;
  closeLabel: string;
  title: ReactNode;
  /** Small line above the title (e.g. "Étape 2/4"). */
  overline?: ReactNode;
  /** Passive status next to the title, typically a state `Tag`. */
  status?: ReactNode;
  /** Text back button shown before the title (desktop navigation back to the list). */
  backLabel?: string;
  onBack?: () => void;
  /** Controls pinned to the right of the top bar, before the close button. */
  actions?: ReactNode;
  /** Secondary column (reference material) shown on the left. Omit it on phones. */
  aside?: ReactNode;
  /** Main working column. */
  children: ReactNode;
  /** Sticky bottom action bar (phones). */
  footer?: ReactNode;
  'data-testid'?: string;
};

const ASIDE_MAX_WIDTH = 680;
const ASIDE_RATIO = '42%';

const Paper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}));

const TopBar = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  minHeight: theme.spacing(7),
  paddingInline: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('sm')]: {
    minHeight: theme.spacing(8),
    paddingInline: theme.spacing(3),
  },
}));

const BackSlot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  paddingRight: theme.spacing(1.5),
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const Heading = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
}));

const Title = styled('h2')(({ theme }) => ({
  ...theme.typography.h6,
  margin: 0,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.h5,
  },
}));

const Overline = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const StatusSlot = styled('div')({
  flexShrink: 0,
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const Body = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  minHeight: 0,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const Aside = styled('section')(({ theme }) => ({
  flexShrink: 0,
  width: ASIDE_RATIO,
  maxWidth: ASIDE_MAX_WIDTH,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: theme.spacing(3),
  borderRight: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxWidth: 'none',
    borderRight: 0,
  },
}));

const Main = styled('section')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
}));

const Footer = styled('footer')(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(2),
  paddingBottom: `max(${theme.spacing(2)}, env(safe-area-inset-bottom))`,
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

/**
 * Full-screen working surface for processing one item (an e-mail, a request) with its reference
 * material beside it. Top bar with title, status and actions; optional aside column; sticky footer.
 */
export function WorkspaceDialog(props: Props) {
  const titleId = useId();

  return (
    <MuiDialog
      fullScreen
      open={props.open}
      onClose={props.onClose}
      aria-labelledby={titleId}
      data-testid={props['data-testid'] ?? 'workspace-dialog'}
    >
      <Paper>
        <TopBar>
          {props.onBack && props.backLabel ? (
            <BackSlot>
              <Button variant="text" color="default" startIcon={<BackIcon />} onClick={props.onBack}>
                {props.backLabel}
              </Button>
            </BackSlot>
          ) : null}

          <Heading>
            {props.overline ? <Overline>{props.overline}</Overline> : null}
            <TitleRow>
              <Title id={titleId}>
                {props.title}
              </Title>
              {props.status ? <StatusSlot>{props.status}</StatusSlot> : null}
            </TitleRow>
          </Heading>

          <Actions>
            {props.actions}
            <IconButton tooltip={props.closeLabel} edge="end" onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </Actions>
        </TopBar>

        <Body>
          {props.aside ? <Aside>{props.aside}</Aside> : null}
          <Main>{props.children}</Main>
        </Body>

        {props.footer ? <Footer>{props.footer}</Footer> : null}
      </Paper>
    </MuiDialog>
  );
}

export type { Props as WorkspaceDialogProps };
